export interface EventHandlerWrapper {
  handlerId: string;
  windowId: string;
  originalHandler: Function;
  handlerStr: string;
  execute: Function;
  isValid: () => boolean;
  // 注册时捕获（闭包时刻的）调用方 realm 与路由，供路由切换时自动清理已卸载页面的监听器。
  realmWindow?: unknown;
  routePath?: string | null;
  persist?: boolean;
}

export interface MachEventStore {
  handlers: { [event: string]: (Function | EventHandlerWrapper)[]; };
  registry: Map<string, Set<string>>;
}

export interface MachEventOnOptions {
  // 路由生命周期之外的长效监听（入口级、跨路由）：路由切换自动清理时跳过。
  persist?: boolean;
}

interface EventOwnerWindow {
  closed: boolean;
  parent: unknown;
}

// SPA 单路由活跃：注册于其他路由的监听器必然属于已卸载页面。
// pathname 做尾斜杠归一化，避免直开 URL 的 / 与 /path/ 差异导致误判。
// 无 window 的环境（node 测试沙箱等）返回 null：对应 wrapper 被自动清理保守跳过。
export function readCurrentRoutePath(): string | null {
  try {
    if (typeof window === 'undefined' || !window?.location) return null;
    return window.location.pathname.replace(/\/+$/, '') || '/';
  } catch (error) {
    return null;
  }
}

function isEventHandlerWrapper(
  handler: Function | EventHandlerWrapper
): handler is EventHandlerWrapper {
  return Boolean(
    handler
    && typeof handler === 'object'
    && 'windowId' in handler
    && 'execute' in handler
  );
}

export function createMachEventManager(
  store: MachEventStore,
  windowId: string,
  ownerWindow: EventOwnerWindow
) {
  let nextHandlerId = 0;

  const syncRegistryForEvent = (event: string) => {
    store.registry.forEach((events, registeredWindowId) => {
      const hasOwnedHandler = (store.handlers[event] || []).some((handler) => (
        isEventHandlerWrapper(handler) && handler.windowId === registeredWindowId
      ));
      if (!hasOwnedHandler) {
        events.delete(event);
        if (events.size === 0) {
          store.registry.delete(registeredWindowId);
        }
      }
    });
  };

  const updateHandlers = (
    event: string,
    handlers: (Function | EventHandlerWrapper)[]
  ) => {
    if (handlers.length === 0) {
      delete store.handlers[event];
    } else {
      store.handlers[event] = handlers;
    }
    syncRegistryForEvent(event);
  };

  const cleanupInvalid = (event?: string) => {
    const eventsToCheck = event ? [event] : Object.keys(store.handlers);
    eventsToCheck.forEach((eventName) => {
      const handlers = store.handlers[eventName];
      if (!Array.isArray(handlers)) return;

      const validHandlers = handlers.filter((handler) => {
        if (isEventHandlerWrapper(handler)) {
          try {
            return handler.isValid();
          } catch (error) {
            return false;
          }
        }
        return typeof handler === 'function';
      });
      if (validHandlers.length === 0 || validHandlers.length !== handlers.length) {
        updateHandlers(eventName, validHandlers);
      }
    });
  };

  const disposeById = (event: string, handlerId: string) => {
    const handlers = store.handlers[event];
    if (!Array.isArray(handlers)) return;

    updateHandlers(event, handlers.filter((handler) => !(
      isEventHandlerWrapper(handler)
      && handler.windowId === windowId
      && handler.handlerId === handlerId
    )));
  };

  const on = (event: string, handler: Function, options?: MachEventOnOptions) => {
    if (!event || !handler) return undefined;

    cleanupInvalid(event);
    const handlers = store.handlers[event] || [];
    const handlerId = `${windowId}_handler_${nextHandlerId++}`;
    const wrappedHandler: EventHandlerWrapper = {
      handlerId,
      windowId,
      originalHandler: handler,
      handlerStr: handler.toString(),
      execute: handler,
      // realmWindow 从调用方上下文（window）读而非 ownerWindow：iframe 内经父页
      // Mach（takeover 后）注册时，ownerWindow 是父页，路由与 realm 都会读错。
      // 无 window 的环境（node 测试沙箱）留 undefined：cleanupStaleRouteHandlers
      // 判定 realmWindow !== window 时跳过，这类 wrapper 不会被自动清理误删。
      realmWindow: typeof window === 'undefined' ? undefined : window,
      routePath: readCurrentRoutePath(),
      persist: options?.persist === true,
      isValid: () => {
        try {
          return !ownerWindow.closed && ownerWindow.parent !== null;
        } catch (error) {
          return false;
        }
      },
    };

    const duplicateIndex = handlers.findIndex((existingHandler) => {
      if (isEventHandlerWrapper(existingHandler)) {
        return existingHandler.windowId === windowId
          && existingHandler.handlerStr === wrappedHandler.handlerStr;
      }
      return existingHandler.toString() === wrappedHandler.handlerStr;
    });

    if (duplicateIndex >= 0) {
      // 无 windowId 的历史 handler 无法证明归属，保留原条目以避免误覆盖其他 iframe。
      if (isEventHandlerWrapper(handlers[duplicateIndex])) {
        handlers[duplicateIndex] = wrappedHandler;
      }
    } else {
      handlers.push(wrappedHandler);
    }
    store.handlers[event] = handlers;

    if (!store.registry.has(windowId)) {
      store.registry.set(windowId, new Set());
    }
    if (handlers.includes(wrappedHandler)) {
      store.registry.get(windowId)!.add(event);
    }

    let disposed = false;
    return () => {
      if (disposed) return;
      disposed = true;
      disposeById(event, handlerId);
    };
  };

  const off = (event: string, handler: Function) => {
    if (!event || !handler) return;
    const handlers = store.handlers[event];
    if (!Array.isArray(handlers)) return;

    updateHandlers(event, handlers.filter((registeredHandler) => {
      if (!isEventHandlerWrapper(registeredHandler)) {
        // 历史 raw handler 没有 iframe 所有权信息，不能安全地由当前 iframe 删除。
        return true;
      }
      if (registeredHandler.windowId !== windowId) return true;
      return registeredHandler.originalHandler !== handler
        && registeredHandler.execute !== handler;
    }));
  };

  const trigger = (event: string, data?: unknown) => {
    cleanupInvalid(event);
    const handlers = store.handlers[event];
    if (!Array.isArray(handlers)) return;

    handlers.slice().forEach((handler) => {
      try {
        if (isEventHandlerWrapper(handler)) {
          if (handler.isValid()) handler.execute(data);
        } else {
          handler(data);
        }
      } catch (error) {
        console.warn('Event handler execution failed:', error);
      }
    });
  };

  const cleanupCurrentWindow = () => {
    Object.keys(store.handlers).forEach((event) => {
      updateHandlers(event, store.handlers[event].filter((handler) => !(
        isEventHandlerWrapper(handler) && handler.windowId === windowId
      )));
    });
    store.registry.delete(windowId);
  };

  // 新路由挂载时调用：同 realm 内、注册于其他路由且未 persist 的 wrapper 全部移除。
  // 跨 realm（iframe 经父页 eventManager 注册）与 routePath 不可读的历史 wrapper 不动，
  // 防止父页路由切换误删 alert iframe 内的监听器。
  const cleanupStaleRouteHandlers = (currentRoutePath: string) => {
    let removed = 0;
    Object.keys(store.handlers).forEach((eventName) => {
      const handlers = store.handlers[eventName];
      if (!Array.isArray(handlers)) return;
      const alive = handlers.filter((handler) => {
        if (
          !isEventHandlerWrapper(handler)
          || handler.persist === true
          || handler.realmWindow !== window
          || handler.routePath == null
        ) {
          return true;
        }
        if (handler.routePath === currentRoutePath) return true;
        removed += 1;
        return false;
      });
      if (alive.length !== handlers.length) {
        updateHandlers(eventName, alive);
      }
    });
    return removed;
  };

  return {
    on,
    off,
    trigger,
    cleanupInvalid,
    cleanupCurrentWindow,
    cleanupStaleRouteHandlers,
  };
}
